import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MenuIcon, XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../Button';
import { LeadClockNavSlot } from '../leadClock/LeadClock';
type NavLink = {
  href: string;
  label: string;
  isRoute?: boolean;
};
const NAV_LINKS: NavLink[] = [{
  href: '/#how',
  label: 'How it works'
}, {
  href: '/#pricing',
  label: 'Pricing'
}, {
  href: '/#faq',
  label: 'FAQ'
}, {
  href: '/blog',
  label: 'Blog',
  isRoute: true
}];
export function Navbar() {
  const [open, setOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  // Close menu on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  // Lock body scroll while menu is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);
  return <nav className="sticky top-0 z-40 w-full bg-white/95 backdrop-blur border-b border-gray-200" data-id="element-186">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between gap-3 sm:gap-6" data-id="element-187">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-2 shrink-0" data-id="element-188">
          <div className="w-7 h-7 rounded-md border border-navy-500 bg-navy-500 text-white flex items-center justify-center text-[11px] font-bold tracking-tight" data-id="element-189">
            S
          </div>
          <span className="text-sm font-semibold text-gray-900 tracking-tight" data-id="element-190">
            speed-to-lead<span className="text-gray-400" data-id="element-191">.ai</span>
          </span>
        </Link>

        {/* Nav links — desktop only */}
        <div className="hidden md:flex items-center gap-6 text-sm text-gray-600" data-id="element-192">
          {NAV_LINKS.map(l => l.isRoute ? <Link key={l.href} to={l.href} className="hover:text-gray-900 transition-colors" data-id="element-193">
                {l.label}
              </Link> : <a key={l.href} href={l.href} className="hover:text-gray-900 transition-colors" data-id="element-194">
                {l.label}
              </a>)}
        </div>

        {/* Right cluster: clock capsule + CTA (desktop) + hamburger (mobile) */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0" data-id="element-195">
          <LeadClockNavSlot data-id="element-196" />

          {/* CTA — hidden on mobile, replaced by hamburger */}
          <div className="hidden md:block" data-id="element-197">
            <Button variant="primary" size="small" rightIcon={<span aria-hidden data-id="element-199">→</span>} className="!bg-navy-500 !border-navy-500 hover:!bg-navy-800 hover:!border-navy-800" data-id="element-198">
              Book a Demo
            </Button>
          </div>

          {/* Hamburger — mobile only */}
          <button type="button" onClick={() => setOpen(v => !v)} aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open} aria-controls="mobile-menu" className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2" data-id="element-200">
            {open ? <XIcon className="w-4 h-4" data-id="element-201" /> : <MenuIcon className="w-4 h-4" data-id="element-202" />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      <AnimatePresence data-id="element-203">
        {open && <>
            {/* Backdrop */}
            <motion.div key="backdrop" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: reduceMotion ? 0 : 0.18
        }} onClick={() => setOpen(false)} className="md:hidden fixed inset-0 top-16 bg-gray-900/30 z-30" aria-hidden data-id="element-204" />

            {/* Panel */}
            <motion.div key="panel" id="mobile-menu" initial={reduceMotion ? {
          opacity: 0
        } : {
          opacity: 0,
          y: -8
        }} animate={reduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} exit={reduceMotion ? {
          opacity: 0
        } : {
          opacity: 0,
          y: -8
        }} transition={{
          duration: reduceMotion ? 0 : 0.2,
          ease: 'easeOut'
        }} className="md:hidden absolute top-16 inset-x-0 z-40 bg-white border-b border-gray-200 shadow-sm" data-id="element-205">
              <div className="px-4 sm:px-6 py-4 flex flex-col gap-1" data-id="element-206">
                {NAV_LINKS.map(l => l.isRoute ? <Link key={l.href} to={l.href} onClick={() => setOpen(false)} className="px-3 py-3 rounded-md text-base font-medium text-gray-900 hover:bg-gray-50 transition-colors" data-id="element-207">
                      {l.label}
                    </Link> : <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="px-3 py-3 rounded-md text-base font-medium text-gray-900 hover:bg-gray-50 transition-colors" data-id="element-208">
                      {l.label}
                    </a>)}
                <div className="h-px bg-gray-200 my-2" aria-hidden data-id="element-209" />
                <Button variant="primary" size="large" className="w-full !bg-navy-500 !border-navy-500 hover:!bg-navy-800 hover:!border-navy-800" rightIcon={<span aria-hidden data-id="element-211">→</span>} onClick={() => setOpen(false)} data-id="element-210">
                  Book a Demo
                </Button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </nav>;
}